import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Activity, Baby, Hand, Apple, MessageCircle, CalendarCheck, ArrowRight, Quote, Compass, HelpCircle, ShieldCheck, Users } from "lucide-react";
import { SERVICES, PROCESS_STEPS, SHORT_TESTIMONIALS } from "../data/siteData";
import { CTABanner } from "../components/CTABanner";
import { ConcernGuide } from "../components/ConcernGuide";
import { SEO } from "../components/SEO";
import { trackEvent } from "../lib/analytics";

const ICONS = { Activity, Baby, Hand, Apple, MessageCircle };
const COLOR_STYLES = {
  sunny: { chip: "bg-sunny-soft text-sunny-dark", icon: "bg-sunny text-navy", card: "bg-sunny-soft" },
  teal: { chip: "bg-teal-soft text-teal-dark", icon: "bg-teal text-navy", card: "bg-teal-soft" },
  butter: { chip: "bg-butter-soft text-navy", icon: "bg-butter text-navy", card: "bg-butter-soft" },
  lilac: { chip: "bg-lilac-soft text-navy", icon: "bg-lilac text-navy", card: "bg-lilac-soft" },
  leaf: { chip: "bg-leaf-soft text-leaf-dark", icon: "bg-leaf text-navy", card: "bg-leaf-soft" },
};
const spring = { type: "spring", stiffness: 200, damping: 20 };

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-14 items-center">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-navy tracking-tight text-balance">
          Embodying what it means to <span className="text-turquoise">thrive</span>
        </h1>
        <p data-testid="hero-subtitle" className="mt-6 text-base sm:text-lg text-navy-light leading-relaxed max-w-xl">
          Pediatric physiotherapy, occupational therapy, speech and language therapy, and feeding therapy in a fresh, fun and child-friendly environment. Every child deserves the opportunity to communicate, move, play and participate with confidence. We can't wait to help your child thrive!
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact"
            onClick={() => trackEvent("book_evaluation_click", { location: "hero", channel: "contact" })}
            data-testid="hero-book-evaluation-btn"
            className="inline-flex items-center justify-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-1 hover:bg-sunny-dark hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
          >
            <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
            Book an Evaluation
          </Link>
          <a
            href="#start-guide"
            data-testid="hero-start-guide-btn"
            className="inline-flex items-center justify-center gap-2 bg-white text-navy font-bold px-8 py-4 rounded-full border-2 border-border transition-all duration-200 hover:-translate-y-1 hover:border-teal hover:text-teal-dark"
          >
            <Compass className="w-5 h-5" strokeWidth={2.25} />
            Not sure where to start?
          </a>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ ...spring, delay: 0.15 }}
        className="relative"
      >
        <div className="absolute -inset-4 bg-butter blob-shape-alt opacity-40 pointer-events-none" />
        <img
          src="/thrive-hero.jpg"
          alt="A Thrive therapist sharing a warm moment with a child"
          data-testid="hero-image"
          className="relative blob-shape w-full aspect-[5/4] object-cover object-[32%_28%] shadow-2xl shadow-orange-500/10"
          fetchPriority="high"
        />
      </motion.div>
    </div>
  </section>
);

const StartGuideSection = () => (
  <section id="start-guide" data-testid="home-start-guide" className="bg-white border-y border-border scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <ConcernGuide />
    </div>
  </section>
);

const ServicesGrid = () => (
  <section data-testid="home-services" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring} className="max-w-2xl">
      <span className="inline-block bg-lime text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">Our Services</span>
      <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">Four services, one location, less stress for families.</h2>
      <p className="mt-4 text-base sm:text-lg text-navy-light leading-relaxed">
        Multidisciplinary care means coordinated care for better outcomes, seamless and continuous communication among therapists, increased convenience for families and referral sources alike.
      </p>
    </motion.div>

    <div className="mt-12 grid sm:grid-cols-2 gap-6">
      {SERVICES.map((service, i) => {
        const Icon = ICONS[service.icon];
        const styles = COLOR_STYLES[service.color];
        return (
          <motion.div
            key={service.slug}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ ...spring, delay: i * 0.08 }}
          >
            <Link
              to={`/services/${service.slug}`}
              data-testid={`home-service-card-${service.slug}`}
              className={`group flex flex-col ${styles.card} rounded-[2rem] border border-border p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10 h-full`}
            >
              <span className={`w-14 h-14 rounded-2xl ${styles.icon} flex items-center justify-center mb-6 transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110`}>
                <Icon className="w-7 h-7" strokeWidth={2.25} />
              </span>
              <span className={`self-start text-[11px] font-extrabold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full ${styles.chip}`}>
                {service.tagline}
              </span>
              <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-navy">{service.fullName}</h3>
              <p className="mt-3 text-sm sm:text-base text-navy-light leading-relaxed">{service.description}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark">
                Learn more
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2.5} />
              </span>
            </Link>
          </motion.div>
        );
      })}
    </div>
  </section>
);

const Process = () => (
  <section data-testid="home-process" className="bg-white border-y border-border mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring} className="max-w-2xl">
        <span className="inline-block bg-lime text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">Our Process</span>
        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">As simple as one, two, three</h2>
        <p className="mt-4 text-base sm:text-lg text-navy-light leading-relaxed">At Thrive, we aim to make the process as simple as possible for busy families.</p>
      </motion.div>
      <div className="mt-12 grid sm:grid-cols-3 gap-6">
        {PROCESS_STEPS.map((step, i) => (
          <motion.div
            key={step.step}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ ...spring, delay: i * 0.12 }}
            data-testid={`process-step-${i + 1}`}
            className="relative bg-cream rounded-[2rem] border border-border p-8 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-navy/5"
          >
            <span className="font-display text-5xl font-semibold text-sunny">{step.step}</span>
            <h3 className="mt-4 font-display text-xl font-semibold text-navy">{step.title}</h3>
            <p className="mt-3 text-sm text-navy-light leading-relaxed">{step.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialTeaser = () => {
  const [quoteIndex, setQuoteIndex] = useState(() => Math.floor(Math.random() * SHORT_TESTIMONIALS.length));

  useEffect(() => {
    const timer = setInterval(() => setQuoteIndex((i) => (i + 1) % SHORT_TESTIMONIALS.length), 6000);
    return () => clearInterval(timer);
  }, []);

  const item = SHORT_TESTIMONIALS[quoteIndex];

  return (
  <section data-testid="home-testimonial" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
    <div className="grid lg:grid-cols-5 gap-10 items-center">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring} className="lg:col-span-2">
        <span className="inline-block bg-butter-soft text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">Patient Feedback</span>
        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">Families who found their village</h2>
        <p className="mt-4 text-base sm:text-lg text-navy-light leading-relaxed">Real words from real parents who watched their children grow in confidence, skills and joy.</p>
        <Link
          to="/testimonials"
          data-testid="home-testimonials-link"
          className="mt-6 inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark group"
        >
          Read more stories
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2.5} />
        </Link>
      </motion.div>
      <motion.blockquote
        initial={{ opacity: 0, y: 32, rotate: 0 }}
        whileInView={{ opacity: 1, y: 0, rotate: -1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ ...spring, delay: 0.1 }}
        className="lg:col-span-3 bg-white rounded-[2rem] border border-border p-8 sm:p-10 shadow-lg shadow-navy/5"
      >
        <Quote className="w-10 h-10 text-sunny fill-sunny" />
        <div className="min-h-[120px] sm:min-h-[108px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={quoteIndex}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              <p data-testid="home-testimonial-quote" className="mt-5 text-base sm:text-lg text-navy leading-relaxed font-medium">
                "{item.quote}"
              </p>
              <footer className="mt-6 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-teal-soft flex items-center justify-center font-display font-semibold text-teal-dark">
                  {item.author.charAt(0)}
                </span>
                <div>
                  <p data-testid="home-testimonial-author" className="font-extrabold text-sm text-navy">{item.author}</p>
                  <p className="text-xs font-bold text-navy-light">{item.service}</p>
                </div>
              </footer>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.blockquote>
    </div>
  </section>
  );
};

const ParentLinks = () => (
  <section data-testid="home-parent-links" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={spring} className="max-w-2xl mb-10">
      <span className="inline-block bg-lilac-soft text-navy font-extrabold text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full">For Parents</span>
      <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">Everything you need, before you even ask</h2>
    </motion.div>
    <div className="grid sm:grid-cols-3 gap-6">
      {[
        { icon: HelpCircle, chip: "bg-teal-soft text-teal-dark", title: "Frequently Asked Questions", text: "Answers to everything parents ask before a first visit - referrals, ages, sessions and more.", to: "/faq", testId: "parent-link-faq" },
        { icon: ShieldCheck, chip: "bg-lilac-soft text-navy", title: "Using Insurance at Thrive", text: "How coverage, referrals, benefits and self-pay work - explained in plain language.", to: "/insurance", testId: "parent-link-insurance" },
        { icon: Users, chip: "bg-sunny-soft text-sunny-dark", title: "Meet Our Team", text: "The caring therapists behind every milestone - get to know the people your child will love working with.", to: "/team", testId: "parent-link-team" },
      ].map((card, i) => (
        <motion.div
          key={card.title}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ ...spring, delay: i * 0.08 }}
        >
          <Link
            to={card.to}
            data-testid={card.testId}
            className="group flex flex-col bg-white rounded-[2rem] border border-border p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10 h-full"
          >
            <span className={`w-13 h-13 inline-flex w-12 h-12 rounded-2xl ${card.chip} items-center justify-center mb-5`}>
              <card.icon className="w-6 h-6" strokeWidth={2.25} />
            </span>
            <h3 className="font-display text-xl font-semibold text-navy">{card.title}</h3>
            <p className="mt-2.5 text-sm text-navy-light leading-relaxed flex-1">{card.text}</p>
            <span className="mt-5 inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark">
              Take a look
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2.5} />
            </span>
          </Link>
        </motion.div>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Thrive Pediatric Therapy | Physio, OT, Speech & Feeding Therapy in Nassau, Bahamas"
        description="Thrive is a pediatric therapy practice in Nassau, Bahamas offering physiotherapy, occupational therapy, speech & language therapy and feeding therapy for children aged 0-18. Book an evaluation today."
      />
      <Hero />
      <ServicesGrid />
      <Process />
      <StartGuideSection />
      <ParentLinks />
      <TestimonialTeaser />
      <CTABanner />
    </div>
  );
}
